import {
    acceptedSummary,
    tentativeSummary,
    declinedSummary,
    proposedNewTimeSummary,
    noResponseInfoText,
} from './CalendarRollUpSummary.locstring.json';
import { loading } from 'owa-locstrings/lib/strings/loading.locstring.json';
import loc, { format } from 'owa-localize';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Spinner } from 'office-ui-fabric-react/lib/Spinner';
import { ControlIcons } from 'owa-control-icons';
import toggleIsCalendarRollUpExpanded from 'owa-mail-reading-pane-store/lib/actions/rollUp/toggleIsCalendarRollUpExpanded';
import CalendarRollUpViewState from 'owa-mail-reading-pane-store/lib/store/schema/CalendarRollUpViewState';
import ReadOnlyCompactRecipientWell from 'owa-readonly-recipient-well/lib/components/ReadOnlyCompactRecipientWell';
import ReadOnlyRecipientViewState from 'owa-readonly-recipient-well/lib/store/schema/ReadOnlyRecipientViewState';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';

import * as React from 'react';

const styles = require('./CalendarRollUp.scss');

export interface CalendarRollUpSummaryProps {
    calendarRollUpViewState: CalendarRollUpViewState;
}

@observer
export default class CalendarRollUpSummary extends React.Component<CalendarRollUpSummaryProps, {}> {
    private addCommaToSummary(summarizeText: string): string {
        return summarizeText ? summarizeText + ', ' : '';
    }

    private renderSummarizeText(): JSX.Element {
        let summarizeText: string = '';
        const {
            acceptedAttendees,
            tentativeAttendees,
            declinedAttendees,
            proposedNewTimeAttendeesCount,
            isExpanded,
        } = this.props.calendarRollUpViewState;
        const chevronIconName = isExpanded ? ControlIcons.ChevronDown : ControlIcons.ChevronRight;

        if (acceptedAttendees.length) {
            summarizeText = format(loc(acceptedSummary), acceptedAttendees.length);
        }

        if (tentativeAttendees.length) {
            summarizeText = this.addCommaToSummary(summarizeText);
            summarizeText += format(loc(tentativeSummary), tentativeAttendees.length);
        }

        if (declinedAttendees.length) {
            summarizeText = this.addCommaToSummary(summarizeText);
            summarizeText += format(loc(declinedSummary), declinedAttendees.length);
        }

        if (proposedNewTimeAttendeesCount) {
            summarizeText = this.addCommaToSummary(summarizeText);
            summarizeText += format(loc(proposedNewTimeSummary), proposedNewTimeAttendeesCount);
        }

        if (!summarizeText) {
            // Now there is an issue that if the meeting is updated, the parent internet message id of previous response messages are
            // changed to the latest updated meeting message. But the meeting summary card is empty since there is no response for the
            // updated meeting. We need to fix the parent internet message id issue in server side.
            // The current work around is to add a string in the summary card.
            summarizeText = loc(noResponseInfoText);
        }

        return (
            <div className={styles.summarizeText}>
                <Icon className={styles.chevronIcon} iconName={chevronIconName} />
                {summarizeText}
            </div>
        );
    }

    private renderAttendeeWell(
        iconName: ControlIcons,
        attendees: SingleRecipientType[]
    ): JSX.Element {
        const readOnlyRecipients = attendees.length ? this.getMailboxList(attendees) : [];

        return readOnlyRecipients.length ? (
            <div className={styles.attendeeWell}>
                <Icon className={styles.statusIcon} iconName={iconName} />
                <ReadOnlyCompactRecipientWell
                    className={styles.attendeeList}
                    noneTypeRecipients={readOnlyRecipients}
                    toRecipients={null}
                    ccRecipients={null}
                    bccRecipients={null}
                    hideTrailingComponentIfNoTruncation={true}
                />
            </div>
        ) : null;
    }

    private getMailboxList(attendees: SingleRecipientType[]): ReadOnlyRecipientViewState[] {
        const result: ReadOnlyRecipientViewState[] = [];
        attendees.forEach(attendee => {
            result.push({
                email: attendee.Mailbox,
            });
        });
        return result;
    }

    private onCollapsedRollUpClicked = () => {
        toggleIsCalendarRollUpExpanded(this.props.calendarRollUpViewState);
    };

    render() {
        const {
            acceptedAttendees,
            tentativeAttendees,
            declinedAttendees,
            isLoading,
        } = this.props.calendarRollUpViewState;
        return isLoading ? (
            <div className={styles.calendarRollUpCollapsedView}>
                <Spinner className={styles.loadingSpinner} />
                <span className={styles.loadingText}>{loc(loading)}</span>
            </div>
        ) : (
            <div
                className={styles.calendarRollUpCollapsedView}
                onClick={this.onCollapsedRollUpClicked}>
                {this.renderSummarizeText()}
                {this.renderAttendeeWell(ControlIcons.Accept, acceptedAttendees)}
                {this.renderAttendeeWell(ControlIcons.Help, tentativeAttendees)}
                {this.renderAttendeeWell(ControlIcons.Cancel, declinedAttendees)}
            </div>
        );
    }
}
